import React from 'react';
import { Card, Typography, Col, Rate, Alert, Space, Statistic } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FieldConfig } from '../../types/fieldTypes';

const { Text } = Typography;

interface PriceEvaluationFieldProps {
  field: FieldConfig;
  value: any;
  onChange: (e: any) => void;
  mode: 'edit' | 'view';
  formData?: Record<string, any>;
}

export const PriceEvaluationField: React.FC<PriceEvaluationFieldProps> = ({
  field,
  mode,
  formData = {},
}) => {
  const { t } = useTranslation();
  const currentPrice = formData.price || 0;
  const currency = field.customProps?.currency || 'EUR';

  const getPriceRating = (price: number) => {
    if (price < 1000) return { rating: 1, label: t('price_eval.very_low') };
    if (price < 5000) return { rating: 2, label: t('price_eval.low') };
    if (price < 15000) return { rating: 3, label: t('price_eval.fair') };
    if (price < 30000) return { rating: 4, label: t('price_eval.good') };
    return { rating: 5, label: t('price_eval.excellent') };
  };

  const priceRating = currentPrice > 0 ? getPriceRating(currentPrice) : null;
  const isUniqueCar = currentPrice > 50000;

  if (mode === 'view') {
    return (
      <Col span={field.layout?.span || 24}>
        <Card size="small" title={field.label}>
          <Text type="secondary">
            {priceRating
              ? `${priceRating.label} (${priceRating.rating}/5)`
              : t('price_eval.no_rating')}
          </Text>
        </Card>
      </Col>
    );
  }

  const colProps = {
    span: field.layout?.span || 24,
    offset: field.layout?.offset || 0,
    order: field.layout?.order,
  };

  return (
    <Col {...colProps} className={field.layout?.className}>
      <Card
        title={
          <Space>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
            {field.label}
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {currentPrice > 0 ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Text type="secondary">
              {t('price_eval.rating_header')}
            </Text>

            <Space align="center" size="large">
              <Statistic
                title={t('price_eval.label_price')}
                value={currentPrice}
                suffix={currency}
                valueStyle={{ color: '#262626', fontSize: 24, fontWeight: 600 }}
              />
              {field.customProps?.showRating && priceRating && (
                <Space direction="vertical" align="center" size="small">
                  <Rate
                    disabled
                    value={priceRating.rating}
                    style={{ color: '#faad14' }}
                  />
                  <Text strong>{priceRating.label}</Text>
                </Space>
              )}
            </Space>

            {field.customProps?.showUniqueCarMessage && isUniqueCar && (
              <Alert
                message={t('price_eval.unique_title')}
                description={t('price_eval.unique_desc')}
                type="warning"
                showIcon
                icon={<InfoCircleOutlined />}
              />
            )}
          </Space>
        ) : (
          <Text type="secondary" italic>
            {t('price_eval.enter_price')}
          </Text>
        )}
      </Card>
    </Col>
  );
};
